import { auth } from "../config/firebase";
import { API_BASE_URL } from "../constants/api";

type RequestMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

interface FetchOptions extends Omit<RequestInit, "method" | "body"> {
  method?: RequestMethod;
  body?: object;
}

const statusCodeToMessage = (statusCode: number) => {
  if (!statusCode) {
    return "Network error";
  }
  switch (statusCode) {
    case 400:
      return "Bad Request";
    case 401:
      return "Unauthorized";
  }
  return `HTTP error ${statusCode}`;
};

export class ApiError extends Error {
  statusCode: number;
  message: string;
  data: unknown;
  constructor(statusCode: number, data: unknown) {
    let message = statusCodeToMessage(statusCode);
    if (data) {
      if (typeof data === "object" && "errorCode" in data) {
        message = data.errorCode as string;
      } else if (typeof data === "object" && "message" in data) {
        message = data.message as string;
      } else if (
        typeof data === "object" &&
        "error" in data &&
        typeof data.error === "string"
      ) {
        message = data.error;
      }
    }
    super(message);
    this.statusCode = statusCode;
    this.message = message;
    this.data = data;
  }
}

const baseFetch = (
  endpoint: string,
  options: FetchOptions & { token?: string | null } = {}
) => {
  const { method = "GET", body, token, ...restOptions } = options;

  const headers = new Headers({
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
    ...restOptions.headers,
  });

  return fetch(`${API_BASE_URL}/${endpoint.replace(/^\//, "")}`, {
    method,
    headers,
    ...(body && { body: JSON.stringify(body) }),
    ...restOptions,
  }).then((response) => {
    if (!response.ok) {
      return response
        .json()
        .then((data) => {
          throw new ApiError(response.status, data);
        })
        .catch((err) => {
          throw new ApiError(response.status, err);
        });
    }
    if (response.status === 204) {
      return null;
    }
    return response.json().catch(() => null);
  });
};

export const apiFetch = (endpoint: string, options: FetchOptions = {}) => {
  const { method = "GET", body, ...restOptions } = options;

  if (!auth.currentUser) {
    throw new Error("User not authenticated");
  }

  return auth.currentUser.getIdToken().then((token) =>
    baseFetch(endpoint, { ...restOptions, method, body, token })
  );
};

export const apiFetchWithoutAuth = (endpoint: string, options: FetchOptions = {}) => {
  const { method = "GET", body, ...restOptions } = options;
  return baseFetch(endpoint, { ...restOptions, method, body });
};

interface FormDataFetchOptions extends Omit<RequestInit, "method" | "body"> {
  method?: RequestMethod;
  body?: FormData;
}

export const apiFetchFormData = (endpoint: string, options: FormDataFetchOptions = {}) => {
  const { method = "POST", body, ...restOptions } = options;

  if (!auth.currentUser) {
    throw new Error("User not authenticated");
  }

  return auth.currentUser
    .getIdToken()
    .then((token) => {
      const headers = new Headers();
      if (token) {
        headers.append("Authorization", `Bearer ${token}`);
      }
      if (restOptions.headers) {
        if (restOptions.headers instanceof Headers) {
          restOptions.headers.forEach((value, key) => {
            headers.append(key, value);
          });
        } else {
          Object.entries(restOptions.headers).forEach(([key, value]) => {
            headers.append(key, value as string);
          });
        }
      }

      return fetch(`${API_BASE_URL}/${endpoint.replace(/^\//, "")}`, {
        method,
        headers,
        ...(body && { body }),
        ...restOptions,
      });
    })
    .then((response) => {
      if (!response.ok) {
        return response
          .json()
          .then((data) => {
            throw new ApiError(response.status, data);
          })
          .catch((err) => {
            throw new ApiError(response.status, err);
          });
      }
      if (response.status === 204) {
        return null;
      }
      return response.json().catch(() => null);
    });
};

export const api = {
  get: <T>(endpoint: string, options?: Omit<FetchOptions, "method" | "body">) =>
    apiFetch(endpoint, { ...options, method: "GET" }) as Promise<T>,

  post: <T>(
    endpoint: string,
    body: object,
    options?: Omit<FetchOptions, "method">
  ) => apiFetch(endpoint, { ...options, method: "POST", body }) as Promise<T>,

  put: <T>(
    endpoint: string,
    body: object,
    options?: Omit<FetchOptions, "method">
  ) => apiFetch(endpoint, { ...options, method: "PUT", body }) as Promise<T>,

  patch: <T>(
    endpoint: string,
    body: object,
    options?: Omit<FetchOptions, "method">
  ) => apiFetch(endpoint, { ...options, method: "PATCH", body }) as Promise<T>,

  delete: <T>(
    endpoint: string,
    options?: Omit<FetchOptions, "method" | "body">
  ) => apiFetch(endpoint, { ...options, method: "DELETE" }) as Promise<T>,
};

